import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ManageOrganizationClient, { BillingSummary } from '../apiClients/ManageOrganizationClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';
import PageTitle from '../components/PageTitle';
import PanelSegment from '../components/PanelSegment';
import useViewportMode from '../hooks/useViewportMode';

type BillingDestination = 'checkout' | 'portal';

const BillingSubscriptionComponent = () => {
    const { mode: viewportMode, isPhone, isDesktop } = useViewportMode();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [summary, setSummary] = useState<BillingSummary>();
    const [busy, setBusy] = useState<boolean>(false);
    const location = useLocation();

    const loadSummary = async (signal?: AbortSignal) => {
        try {
            setBusy(true);
            setSummary(await new ManageOrganizationClient().GetBillingSummary(signal));
        } catch (error) {
            if (!signal?.aborted)
                setError(error, 'Failed to load billing information');
        } finally {
            if (!signal?.aborted)
                setBusy(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        loadSummary(controller.signal);
        return () => controller.abort();
        // Initial loading is intentionally tied to mounting; manual refreshes use the button below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const redirectToBilling = async (destination: BillingDestination) => {
        try {
            setBusy(true);
            const client = new ManageOrganizationClient();
            const response = destination === 'checkout'
                ? await client.CreateBillingCheckout()
                : await client.CreateBillingPortal();

            if (!response?.Url)
                throw new Error('The billing service did not return a redirect URL.');

            window.location.assign(response.Url);
        } catch (error) {
            setError(error, destination === 'checkout'
                ? 'Failed to start subscription setup'
                : 'Failed to open billing management');
            setBusy(false);
        }
    };

    const returnState = new URLSearchParams(location.search).get('billing');
    const periodEnd = summary?.CurrentPeriodEnd
        ? dayjs(summary.CurrentPeriodEnd).format('MMMM D, YYYY')
        : 'Not available';
    const status = summary?.Status || (summary?.HasSubscription ? 'Pending' : 'Not subscribed');

    return <>
        <ErrorDisplay />
        <BusySpinner component={Paper} busy={busy}>
            <PanelSegment titleContent={<PageTitle title="Subscription & Billing" />} fillHeight>
                <Box data-viewport-mode={viewportMode} sx={{
                    p: { xs: 2, md: 3 },
                    pb: !isDesktop ? 'calc(20px + env(safe-area-inset-bottom, 0px))' : undefined,
                    maxWidth: isPhone ? 680 : 1200,
                    mx: 'auto',
                    minHeight: !isDesktop ? '100%' : undefined,
                }}>
                    <Stack spacing={3}>
                        {returnState === 'success' &&
                            <Alert severity="success">Billing setup was completed. Status can take a moment to update while the payment confirmation is processed.</Alert>}
                        {returnState === 'cancelled' &&
                            <Alert severity="info">Billing setup was cancelled. No changes were made.</Alert>}

                        <Alert severity="info">
                            Payment methods, invoices, and banking details are handled on the secure hosted billing page. Synergist Pro never collects or stores your payment details.
                        </Alert>

                        <Box sx={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', alignItems: isPhone ? 'flex-start' : 'center', gap: 2, flexWrap: 'wrap' }}>
                            <AccountBalanceWalletOutlinedIcon color="primary" sx={{ fontSize: 42 }} />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h4">Organization subscription</Typography>
                                <Typography color="text.secondary">
                                    Billing is based on teammates who are active in Synergist Pro. Pricing and billable counts are calculated by the web service.
                                </Typography>
                            </Box>
                            <Chip
                                label={status}
                                color={summary?.IsActive ? 'success' : summary?.HasSubscription ? 'warning' : 'default'}
                                variant={summary?.IsActive ? 'filled' : 'outlined'}
                            />
                        </Box>

                        <Grid container spacing={2}>
                            <SummaryCard label="Active teammates" value={summary?.ActiveTeammateCount} />
                            <SummaryCard label="Minimum billed teammates" value={summary?.MinimumTeammateCount} />
                            <SummaryCard label="Billed teammates" value={summary?.BilledTeammateCount} />
                            <SummaryCard label="Price per teammate" value={summary?.UnitPriceDisplay} />
                            <SummaryCard label="Estimated monthly total" value={summary?.EstimatedMonthlyTotalDisplay} emphasize />
                            <SummaryCard label="Current paid period ends" value={periodEnd} />
                        </Grid>

                        {summary?.CancelAtPeriodEnd &&
                            <Alert severity="warning">This subscription is scheduled to end after the current paid period on {periodEnd}.</Alert>}

                        <Box sx={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', gap: 2, flexWrap: 'wrap', '& .MuiButton-root': { minHeight: isDesktop ? undefined : 44, width: isPhone ? '100%' : 'auto' } }}>
                            {!summary?.HasSubscription &&
                                <Button
                                    variant="contained"
                                    endIcon={<LaunchIcon />}
                                    onClick={() => redirectToBilling('checkout')}>
                                    Set up subscription
                                </Button>}
                            {summary?.HasSubscription && summary?.CanManageBilling &&
                                <Button
                                    variant="contained"
                                    endIcon={<LaunchIcon />}
                                    onClick={() => redirectToBilling('portal')}>
                                    Manage payment & billing
                                </Button>}
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => loadSummary()}>
                                Refresh status
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </PanelSegment>
        </BusySpinner>
    </>;
};

interface SummaryCardProps {
    label: string;
    value?: string | number;
    emphasize?: boolean;
}

const SummaryCard = ({ label, value, emphasize = false }: SummaryCardProps) => {
    const { isTablet, isLandscape, isDesktop } = useViewportMode();
    return <Grid item xs={12} sm={isTablet ? (isLandscape ? 4 : 6) : 6} md={isDesktop ? 4 : undefined}>
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="overline" color="text.secondary">{label}</Typography>
                <Typography variant={emphasize ? 'h3' : 'h4'} color={emphasize ? 'primary' : 'text.primary'}>
                    {value ?? 'Not available'}
                </Typography>
            </CardContent>
        </Card>
    </Grid>;
};

export default BillingSubscriptionComponent;
